import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Save, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateSaisie } from "@/components/DateField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/KpiCard";
import { api, errMsg, money } from "@/lib/api";

const AN = new Date().getFullYear();
const ANNEES = [AN + 1, AN, AN - 1].map(String);
const STATUT = { paye: ["Payé", "text-emerald-700"], probable: ["Paiement probable (même montant) : comptez-le pour « Acompte » dans la conciliation pour confirmer", "text-amber-700"], retard: ["Aucun paiement trouvé", "text-red-600"], a_venir: ["À venir", "text-slate-600"] };
const fr = (v) => Number(v).toFixed(2).replace(".", ",");
const nb = (v) => Number(String(v).replace(/\s/g, "").replace(",", "."));

const Lecture = ({ d }) => (
  <div className="space-y-1" data-testid="acomptes-liste">
    {d.acomptes.map((a, i) => {
      const [lib, cls] = STATUT[a.statut];
      return (
        <div key={i} className="flex flex-wrap items-center gap-3 border-t border-slate-100 py-1.5 text-sm" data-testid={`acompte-${i}`}>
          <span className="w-24 font-mono text-slate-600">{a.date}</span>
          <span className="w-28 tabular font-medium">{money(a.montant)}</span>
          <span className={`text-xs ${cls}`} data-testid={`acompte-${i}-statut`}>{lib}{a.statut === "a_venir" ? ` · dans ${a.jours} j` : ""}</span>
          {a.paiement && <span className="text-xs text-slate-500">{a.paiement.date} · {a.paiement.description} · {money(a.paiement.montant)}</span>}
          {a.etat?.termine && <span className="w-full text-xs text-slate-500 sm:w-auto" data-testid={`acompte-${i}-etat`}>État réel {a.etat.libelle} : TPS {money(a.etat.tps)} · TVQ {money(a.etat.tvq)} · net {money(a.etat.net)}</span>}
        </div>
      );
    })}
    <p className="pt-1 text-sm font-semibold" data-testid="acomptes-total">Total {money(d.total)} · payé {money(d.paye)}</p>
  </div>
);

const Edition = ({ d, onFini }) => {
  const [lignes, setLignes] = useState(d.acomptes.map((a) => ({ date: a.date, montant: fr(a.montant) })));
  const p = d.proposition;
  const maj = (i, k, v) => setLignes(lignes.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const enregistrer = async () => {
    const acomptes = lignes.map((x) => ({ date: x.date, montant: nb(x.montant) }));
    if (acomptes.some((a) => !a.date || !(a.montant > 0))) { toast.error("Date ou montant invalide"); return; }
    try { await api.put(`/acomptes-taxes/${d.annee}`, { acomptes }); toast.success("Acomptes enregistrés"); onFini(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const utiliser = (liste) => setLignes(liste.map((a) => ({ date: a.date, montant: fr(a.montant) })));
  const reels = p.reel.filter((r) => r.termine);
  const avecReel = () => [...reels.filter((r) => r.montant > 0), ...p.acomptes.slice(reels.length)];
  return (
    <div className="space-y-2" data-testid="acomptes-edition">
      <div className="space-y-2 rounded-md bg-slate-50 p-3 text-xs text-slate-600" data-testid="acomptes-proposition">
        <p>1) 1/4 de la taxe nette {p.annee_base} ({p.source}) : TPS {money(p.base_tps)} → {money(p.part_tps)} + TVQ {money(p.base_tvq)} → {money(p.part_tvq)} = <b>{money(p.acomptes[0].montant)}</b> par acompte, aux échéances {p.acomptes.map((a) => a.date).join(", ")}.
          {!p.obligatoire_tps && !p.obligatoire_tvq && " Sous 3 000 $ de taxe nette l'année précédente, les acomptes ne sont en principe pas obligatoires."}
          <Button size="sm" variant="outline" className="ml-2 h-7" onClick={() => utiliser(p.acomptes)} data-testid="acomptes-utiliser-proposition"><Wand2 className="h-3.5 w-3.5 mr-1" />Utiliser ce calcul</Button></p>
        <div data-testid="acomptes-reel">2) État réel de chaque trimestre {d.annee} (taxes perçues − déductibles) :
          {p.reel.map((r) => <span key={r.trimestre} className="block" data-testid={`acomptes-reel-T${r.trimestre}`}>T{r.trimestre} → échéance {r.date} : {r.termine ? `TPS ${money(r.tps)} · TVQ ${money(r.tvq)} · net ${money(r.montant)}${r.montant > 0 ? "" : " (aucun acompte)"}` : "trimestre pas encore terminé"}</span>)}
          {reels.length > 0 && <Button size="sm" variant="outline" className="mt-1 h-7" onClick={() => utiliser(avecReel())} data-testid="acomptes-utiliser-reel"><Wand2 className="h-3.5 w-3.5 mr-1" />Utiliser le réel des trimestres terminés</Button>}
        </div>
      </div>
      {lignes.map((x, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <DateSaisie value={x.date} onChange={(v) => maj(i, "date", v)} className="h-8 w-40" data-testid={`acompte-edit-${i}-date`} />
          <Input value={x.montant} onChange={(e) => maj(i, "montant", e.target.value)} inputMode="decimal" className="h-8 w-32 tabular" data-testid={`acompte-edit-${i}-montant`} />
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setLignes(lignes.filter((_, j) => j !== i))} data-testid={`acompte-edit-${i}-retirer`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setLignes([...lignes, { date: "", montant: "" }])} data-testid="acomptes-ajouter"><Plus className="h-3.5 w-3.5 mr-1" />Acompte</Button>
        <Button size="sm" onClick={enregistrer} className="bg-emerald-700 hover:bg-emerald-800" data-testid="acomptes-enregistrer"><Save className="h-3.5 w-3.5 mr-1" />Enregistrer</Button>
        <Button size="sm" variant="ghost" onClick={() => onFini(false)} data-testid="acomptes-annuler">Annuler</Button>
      </div>
    </div>
  );
};

// Acomptes TPS/TVQ de l'année (selon l'avis reçu ou le calcul proposé), avec le paiement trouvé dans les relevés
export const AcomptesTaxesPanel = ({ version }) => {
  const [annee, setAnnee] = useState(String(AN));
  const [d, setD] = useState(null);
  const [edition, setEdition] = useState(false);
  const [v, setV] = useState(0);
  useEffect(() => { api.get("/acomptes-taxes", { params: { annee } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [annee, version, v]);
  useEffect(() => {
    if (d && window.location.hash === "#acomptes") { document.querySelector("[data-testid=acomptes-panel]")?.scrollIntoView({ behavior: "smooth" }); }
  }, [d]);
  const action = (
    <div className="flex shrink-0 items-center gap-2">
      <Select value={annee} onValueChange={(a) => { setD(null); setEdition(false); setAnnee(a); }}><SelectTrigger className="h-8 w-24" data-testid="acomptes-annee"><SelectValue /></SelectTrigger>
        <SelectContent>{ANNEES.map((a) => <SelectItem key={a} value={a} data-testid={`acomptes-annee-${a}`}>{a}</SelectItem>)}</SelectContent></Select>
      {d && !edition && <Button size="sm" variant="outline" onClick={() => setEdition(true)} data-testid="acomptes-modifier"><Pencil className="h-3.5 w-3.5 mr-1" />{d.acomptes.length ? "Modifier" : "Saisir"}</Button>}
    </div>
  );
  return (
    <Panel title="Acomptes TPS/TVQ" testId="acomptes-panel" action={action} className="scroll-mt-48">
      {!d ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <>
          <p className="mb-3 text-xs text-slate-500">Dates et montants des acomptes à verser à Revenu Québec pour {d.annee}. Un rappel part par courriel 14 jours et 3 jours avant chaque échéance non payée. Un paiement « TPS &amp; TVQ » est reconnu s'il est compté pour « Acompte {d.annee} » ou s'il a le même montant à ±45 jours.</p>
          {edition ? <Edition d={d} onFini={(ok) => { setEdition(false); if (ok) { setV((x) => x + 1); } }} />
            : d.acomptes.length ? <Lecture d={d} /> : <p className="text-sm text-slate-500" data-testid="acomptes-aucun">Aucun acompte saisi pour {d.annee}. Cliquez sur « Saisir » pour entrer ceux de l'avis ou utiliser le calcul proposé.</p>}
        </>
      )}
    </Panel>
  );
};
